/**
 * Per-route SEO helper.
 *
 * Sets canonical URL, title, description, og:url, hreflang, and html-lang tags
 * based on the current route. Use this on any top-level page so Google can
 * distinguish each URL (avoids "Duplicate without user-selected canonical"
 * GSC issues) and surface the right language to international users.
 *
 * Usage:
 *   <Seo path="/library" title="Phrase Library — ClearCommunicationApp"
 *        description="Browse 3,000+ neurodivergent-friendly phrases..." />
 *
 * If `path` is omitted, falls back to `window.location.pathname`.
 */
import { Helmet } from '@dr.pogodin/react-helmet';

const SITE_ORIGIN = 'https://clearcommunicationapp.com';

/** Read current language from localStorage without using the React context.
 *  Avoids hooks-rules issues when Seo is rendered outside LanguageProvider
 *  (e.g., in a 404 boundary). Defaults to 'en'. */
const getLangCode = () => {
  if (typeof window === 'undefined') return 'en';
  try {
    return window.localStorage.getItem('definitionLanguage') === 'es' ? 'es' : 'en';
  } catch {
    return 'en';
  }
};

export const Seo = ({ path, title, description, image, imageWidth, imageHeight, type = 'website' }) => {
  const langCode = getLangCode();

  const currentPath =
    path !== undefined
      ? path
      : (typeof window !== 'undefined' ? window.location.pathname : '/');

  // Strip query strings, hashes, and any trailing slash (except root) to keep
  // canonicals consistent regardless of how a URL was reached.
  let normalized = currentPath.split('?')[0].split('#')[0];
  if (normalized.length > 1 && normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1);
  }
  const canonicalUrl = `${SITE_ORIGIN}${normalized || '/'}`;
  // Distinct alternate URL for the Spanish version. LanguageContext reads
  // `?lang=es` on mount so this is a real, crawlable, differently-rendered page.
  const esUrl = `${canonicalUrl}?lang=es`;
  const enUrl = canonicalUrl;

  // Resolve `image` to an absolute URL if a relative path was passed —
  // social crawlers reject relative og:image values.
  const absoluteImage = image
    ? (image.startsWith('http') ? image : `${SITE_ORIGIN}${image.startsWith('/') ? '' : '/'}${image}`)
    : null;

  return (
    <Helmet>
      {/* Set <html lang="..."> dynamically so screen readers and Google know
          the active content language. */}
      <html lang={langCode} />
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonicalUrl} />

      {/* hreflang annotations — `?lang=es` is a crawlable variant that
          renders the Spanish version on initial mount (LanguageContext
          reads the URL param). x-default points at the EN canonical. */}
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="es" href={esUrl} />
      <link rel="alternate" hrefLang="x-default" href={enUrl} />

      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content={langCode === 'es' ? 'es_US' : 'en_US'} />
      <meta property="og:locale:alternate" content={langCode === 'es' ? 'en_US' : 'es_US'} />
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:type" content={type} />
      {absoluteImage && <meta property="og:image" content={absoluteImage} />}
      {absoluteImage && <meta property="og:image:secure_url" content={absoluteImage} />}
      {absoluteImage && imageWidth && <meta property="og:image:width" content={String(imageWidth)} />}
      {absoluteImage && imageHeight && <meta property="og:image:height" content={String(imageHeight)} />}
      <meta name="twitter:card" content="summary_large_image" />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      {absoluteImage && <meta name="twitter:image" content={absoluteImage} />}
    </Helmet>
  );
};

export default Seo;
